import { Box, Button, Dialog, makeStyles } from "@material-ui/core"
import { useEffect, useState, KeyboardEvent } from "react";
import Http from "../engine/Http";
import ActionConfirmationDialog from "./ActionConfirmationDialog";
import { ClassNameMap } from "@material-ui/core/styles/withStyles";
import useAuthenticationHeader from "../security/AuthenticationHeader.hook";

export enum DetailsMode {
  Create,
  Update
}

export interface DetailsProps {
  instanceId: number|null;
  afterSave: (responseData: object, closeDialog: boolean) => void;
  onCancel: () => void;
  reportAlert?: (message: string) => void;
  reportAlerts?: (messages: string[]) => void;
};

interface Props<T> extends DetailsProps {
  resource: string;
  mode?: DetailsMode;
  forceSaveOnValidationFailedMessage?: string;
  modificationDisabled?: boolean;
  classes?: ClassNameMap<'dialogPaper'>;
  renderFields: (initialData: T|undefined, handleSaveClicked: (afterSave?: (responseData: T) => void) => void) => JSX.Element;
  initializeFields: (data: T, initial: boolean) => void;
  collectData: () => T|null;
  validateData?: (data: T) => Promise<boolean>;
  renderBottomRegion?: (fetchDetails: (initial: boolean) => void) => JSX.Element;
}

const useStyles = makeStyles(theme => ({
  dialogPaper: {
    minWidth: 640
  },
  dialogBox: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(3)
  },
  button: {
    marginTop: theme.spacing(1)
  }
}));

export const calculateMode = (instanceId: number|null) => instanceId === null ? DetailsMode.Create : DetailsMode.Update;

const DetailsDialog = <T,>(props: Props<T>) => {
  const classes = useStyles();
  const authenticationHeader = useAuthenticationHeader();
  const overridingClasses = props.classes;
  const resource = props.resource;
  const instanceId = props.instanceId;
  const mode = props.mode === undefined ? calculateMode(instanceId) : props.mode;
  const [fetchedData, setFetchedData] = useState<T>();
  const [isForceDuplicateSaveDialogOpen, setForceDuplicateSaveDialogOpen] = useState<boolean>(false);
  const forceSaveOnValidationFailedMessage = props.forceSaveOnValidationFailedMessage;

  const showForceDuplicateSaveDialog = () => setForceDuplicateSaveDialogOpen(true);
  const hideForceDuplicateSaveDialog = () => setForceDuplicateSaveDialogOpen(false);

  const fetchDetails = (initial: boolean) => {
    Http.get(`/api/${resource}/${instanceId}`, [ authenticationHeader ])
      .then(data => {
        console.debug(`Instance of ${resource} with id ${instanceId} fetched: ${JSON.stringify(data)}`);
        props.initializeFields(data, initial);
        setFetchedData(data);
      })
      .catch(reason => console.error(`Could fetch instance of ${resource} with id ${instanceId}: ${reason}`));
  };

  const saveData = (data: T, afterSave?: (data: T) => void) => {
    const operation = instanceId
      ? { promise: Http.put(`api/${resource}/${instanceId}`, data, [ authenticationHeader ]), name: 'update' }
      : { promise: Http.post(`api/${resource}`, data, [ authenticationHeader ]), name: 'create' };
    operation.promise
      .then(data => {
        console.debug(`Instance of ${resource} ${operation.name}d: ${JSON.stringify(data)}`);
        if (afterSave) {
          afterSave(data);
          props.afterSave(data, false);
        } else {
          props.afterSave(data, true);
        } 
      })
      .catch(reason => {
        console.error(`Could not ${operation.name} ${resource}: ${reason}`);
        props.onCancel();
      });
  };

  const handleSaveClicked = async (afterSave?: (data: T) => void) => {
    const data = props.collectData();
    if (data) {
      if (props.validateData && await props.validateData(data) === false) {
        console.debug(`${resource} validation failed`);
        showForceDuplicateSaveDialog();
      } else {
        saveData(data, afterSave);
      }
    } else {
      console.debug(`${resource} not saved due to missing data`);
    }
  };

  const handleCancelClicked = () => props.onCancel();

  const handleDuplicateSaveConfirmation = () => {
    const data = props.collectData();
    if (data) {
      saveData(data);
    } else {
      console.debug(`${resource} not saved forcily due to missing data`);
    }
    hideForceDuplicateSaveDialog();
  };

  const handleKeyPressed = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleSaveClicked();
    }
  };

  const handleKeyUp = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      handleCancelClicked();
    }
  };

  useEffect(() => {
    if (instanceId !== null) {
      fetchDetails(true);
    }
  }, []);

  return (
    <>
      <Dialog open={true} classes={{ paper: overridingClasses ? overridingClasses.dialogPaper : classes.dialogPaper }}>
        <Box className={classes.dialogBox} onKeyPress={handleKeyPressed} onKeyUp={handleKeyUp}>
          {(mode === DetailsMode.Create || fetchedData) && props.renderFields(fetchedData, handleSaveClicked)}
          <Button
            color='primary'
            className={ classes.button }
            onClick={ () => handleSaveClicked() }
            variant='contained'
            disabled={ props.modificationDisabled }
          >
            Zapisz i zamknij
          </Button>
          <Button
            color='secondary'
            className={classes.button}
            onClick={handleCancelClicked}
            variant='contained'>
              Anuluj wprowadzanie
          </Button>
          {props.renderBottomRegion && props.renderBottomRegion(fetchDetails)}
        </Box>
      </Dialog>
      <ActionConfirmationDialog
        open={forceSaveOnValidationFailedMessage !== undefined && isForceDuplicateSaveDialogOpen}
        message={forceSaveOnValidationFailedMessage || "Czy wymusić zapis?"}
        confirmButtonLabel="Zapisz pomimo to"
        cancelButtonLabel="Zrezygnuj"
        onCancel={hideForceDuplicateSaveDialog}
        onConfirm={handleDuplicateSaveConfirmation}
      />
    </>
  );
};
export default DetailsDialog;